/**
 * Renders one {@link StreamItem} — the leaf of both the live run and persisted
 * history (same model, one renderer). Text is shown verbatim; a tool item is a
 * small activity chip; a media item is a "generating…" placeholder the WS relay
 * later swaps for the finished asset; a directive delegates to the generative-UI
 * dispatcher, wiring its round-trip to the run.
 */
import { Icon, MediaTile, Spinner, useT } from '@4f/ui';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { GenerativeUi, type GenerativeUiHandlers } from '../generative-ui/GenerativeUi.tsx';
import { FakedMedia } from './FakedMedia.tsx';
import type { StreamItem } from './stream-model.ts';
import { toolLabelKey } from './tool-labels.ts';

/** The run-scoped callbacks the stream threads into each directive item. */
export interface StreamHandlers {
  /** Echo an answer back to the parked run for the directive `directiveId`. */
  submit(directiveId: string, value: unknown): void;
  /** Ingest a concept image for a `file` directive. */
  onFile?(directiveId: string, file: File): void;
  /** Open the shared Artifacts lightbox (a `gallery` directive). */
  onOpenGallery?(assetIds: readonly string[], startIndex: number): void;
  /** Open a modal surface (a `modal` directive). */
  onOpenSurface?: GenerativeUiHandlers['onOpenSurface'];
  /** Resolve a `gallery` directive's asset ids to fetchable URLs. */
  assetUrl?: GenerativeUiHandlers['assetUrl'];
}

/** Narrow a stream item to one `kind`, or `undefined` — feeds `<Match when>`. */
function itemOf<K extends StreamItem['kind']>(
  item: StreamItem,
  kind: K,
): Extract<StreamItem, { kind: K }> | undefined {
  return item.kind === kind ? (item as Extract<StreamItem, { kind: K }>) : undefined;
}

export interface StreamItemViewProps {
  readonly item: StreamItem;
  readonly handlers: StreamHandlers;
}

export function StreamItemView(props: StreamItemViewProps): JSX.Element {
  const { t } = useT();

  return (
    <Switch>
      <Match when={itemOf(props.item, 'text')}>
        {(item) => <span class="f4-stream__text">{item().text}</span>}
      </Match>

      <Match when={itemOf(props.item, 'tool')}>
        {(item) => (
          // `data-tool` keeps the raw name for styling/debugging; the TEXT the
          // user (and a screen reader) gets is the humanised, translated line.
          <span class="f4-stream__tool" data-tool={item().toolName}>
            <Show when={item().status === 'running'} fallback={<Icon name="check" size="sm" />}>
              <Spinner size="sm" label={t('surface.working')} />
            </Show>
            <span class="f4-stream__tool-name">{t(toolLabelKey(item().toolName))}</span>
          </span>
        )}
      </Match>

      <Match when={itemOf(props.item, 'media')}>
        {(item) => (
          <Show
            when={item().faked && item().src}
            fallback={
              // Real job (or not-yet-resolved): a plain placeholder the WS relay
              // swaps for the finished e2 asset (PR8 F6). NOT on the fake timer.
              <MediaTile
                kind={item().ref.kind}
                src={item().src}
                loading={item().src === undefined}
                alt={
                  item().src !== undefined
                    ? t('surface.revealedMedia')
                    : item().ref.kind === 'video'
                      ? t('surface.generatingVideo')
                      : t('surface.generatingImage')
                }
              />
            }
          >
            {(src) => <FakedMedia kind={item().ref.kind} src={src()} />}
          </Show>
        )}
      </Match>

      <Match when={itemOf(props.item, 'directive')}>
        {(item) => (
          <GenerativeUi
            directive={item().directive}
            handlers={{
              onSubmit: (value) => props.handlers.submit(item().id, value),
              onFile: props.handlers.onFile
                ? (file) => props.handlers.onFile?.(item().id, file)
                : undefined,
              onOpenGallery: props.handlers.onOpenGallery,
              onOpenSurface: props.handlers.onOpenSurface,
              assetUrl: props.handlers.assetUrl,
              disabled: item().answered,
            }}
          />
        )}
      </Match>
    </Switch>
  );
}
